import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowLeft,
  Calendar,
  Download,
  FileText,
  Loader2,
  Package,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Ban
} from 'lucide-react';
import config from '../../../config';

export default function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrder = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error("Session expirée");

      const response = await axios.get(`${config.REACT_APP_API_URL}/orderById/${id}`, {
        headers: { Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY }
      });

      setOrder(response.data);
    } catch (err) {
      const msg = err.response?.data?.message || "Impossible de charger les détails de la commande";
      setError(msg);
      console.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchOrder();
  }, [id]);

  const handleDownloadBon = async () => {
    if (!order?.file || order.file === '/file/boncommande/') {
      toast.info("Aucun bon de commande disponible");
      return;
    }

    try {
      const fullUrl = `${config.REACT_APP_API_URL}${order.file}`;
      const response = await axios.get(fullUrl, {
        responseType: 'blob',
        headers: { 'x-api-key': config.SITE_API_KEY }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `bon-commande-${order.code_orders || 'commande'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Bon de commande téléchargé");
    } catch (err) {
      console.error("Échec du téléchargement du bon de commande");
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('terminée') || s.includes('terminé')) {
      return {
        bg: 'bg-emerald-100 dark:bg-emerald-900/40',
        text: 'text-emerald-800 dark:text-emerald-300',
        icon: CheckCircle2,
        label: 'Terminée'
      };
    }
    if (s.includes('en cours')) {
      return {
        bg: 'bg-amber-100 dark:bg-amber-900/40',
        text: 'text-amber-800 dark:text-amber-300',
        icon: Clock,
        label: 'En cours'
      };
    }
    if (s.includes('en attente')) {
      return {
        bg: 'bg-red-100 dark:bg-red-900/40',
        text: 'text-red-800 dark:text-red-300',
        icon: AlertTriangle,
        label: 'En attente'
      };
    }
    return {
      bg: 'bg-gray-100 dark:bg-gray-800',
      text: 'text-gray-700 dark:text-gray-300',
      icon: Ban,
      label: status || 'Indéfini'
    };
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 text-emerald-600 animate-spin" />
        <p className="text-gray-600 dark:text-gray-400">Chargement des détails de la commande...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-8 text-center max-w-2xl mx-auto">
        <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-red-800 dark:text-red-300 mb-3">
          Erreur
        </h2>
        <p className="text-red-700 dark:text-red-400 mb-6">{error || "Commande introuvable"}</p>
        <button
          onClick={() => navigate('/orders')}
          className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  const statusInfo = getStatusBadge(order.etat);

  return (
    <div className="min-h-screen pb-12">
      <ToastContainer position="top-right" theme="colored" limit={3} />

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 dark:bg-gray-950/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 px-4 min-sm:px-6 min-lg:px-8 py-4">
        <div className="max-w-9xl mx-auto flex flex-col min-sm:flex-row min-sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/orders')}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Retour"
            >
              <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            </button>
            <div>
              <h1 className="text-lg min-sm:text-xl font-bold text-gray-900 dark:text-white tracking-tight mb-0">
                Commande {order.code_orders || '—'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {order.file && order.file !== '/file/boncommande/' && (
              <button
                onClick={handleDownloadBon}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
              >
                <Download size={16} />
                Bon de commande
              </button>
            )}

            <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium ${statusInfo.bg} ${statusInfo.text}`}>
              <statusInfo.icon size={16} />
              {statusInfo.label}
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-9xl mx-auto px-4 min-sm:px-6 min-lg:px-8 pt-8">
        <div className="grid grid-cols-1 min-lg:grid-cols-3 gap-6">
          {/* Colonne principale - Produits */}
          <div className="min-lg:col-span-2 space-y-6">
            <div className="dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-lg min-sm:text-xlfont-semibold text-gray-900 dark:text-white flex items-center gap-3">
                  <Package className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  Produits commandés
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">N°</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Produit</th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Qté</th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Unité</th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Image</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {order.items?.length > 0 ? (
                      order.items.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {item.productId?.name || '—'}
                            </div>
                            {item.productId?.reference && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Réf. {item.productId.reference}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center text-sm font-medium text-gray-900 dark:text-white">
                            {item.quantity || 0}
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-600 dark:text-gray-400">
                            {item.productId?.unit?.libelle || '—'}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="inline-block w-12 h-12 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                              <img
                                src={
                                  item.productId?.picture && item.productId.picture !== '/picture/product/'
                                    ? `${config.REACT_APP_API_URL}${item.productId.picture}`
                                    : '/img/product.jpg'
                                }
                                alt={item.productId?.name || 'Produit'}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = '/img/product.jpg'; }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400">
                          Aucun produit dans cette commande
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Colonne latérale - Informations commande */}
          <div className="space-y-6">
            {/* Carte statut */}
            <div className="dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                État de la commande
              </h3>
              <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-base font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                <statusInfo.icon size={20} />
                {statusInfo.label}
              </div>
            </div>

            {/* Infos générales */}
            <div className="dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Informations</h3>
              <dl className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">N° commande</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{order.code_orders || '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Date de création</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {order.date ? new Date(order.date).toLocaleString('fr-TN', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    }) : '—'}
                  </dd>
                </div>
                {order.updatedAt && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Dernière mise à jour</dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {new Date(order.updatedAt).toLocaleString('fr-TN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}